import {intersection, isUndefined, partition} from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {buildAddTeammateSettingsPath} from '../../../../../../helpers/modelHelpers';
import {isTeammateConversation} from '../../../../../../helpers/models/conversationHelpers';
import {ViewerDropdownInterfaceEventSourcesEnum} from '../../../../../../helpers/models/tagHelpers';
import {ResolveDispatchProps} from '../../../../../../helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../helpers/reselectHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {
  BulkActionsTrackingTypesEnum,
  InterfaceEventsEnum,
  InviteTeammatePopoverAccessPointsEnum,
  trackInterfaceEvent,
} from '../../../../../../services/tracking';
import {AppState} from '../../../../../../store/appState';
import {getCompanyUrl, getIsInTrial} from '../../../../../../store/data/company/companySelectors';
import {
  assignToTeammateAction,
  assignToTeammateBulkAction,
} from '../../../../../../store/data/conversations/assigneeConversationsActions';
import {makeGetAssigneeForAll} from '../../../../../../store/data/conversations/conversationsMultiSelectors';
import {getUserOwnedInboxes} from '../../../../../../store/data/inboxes/inboxesSelectors';
import {makeGetIsTeamMemberForConversations} from '../../../../../../store/data/teammates/mentionTeammatesSelectors';
import {getFindOnlineStatus} from '../../../../../../store/data/teammates/onlineTeammatesSelectors';
import {getIsUserGlobalAdmin, getUser} from '../../../../../../store/data/teammates/teammatesSelectors';
import {getFirstTeamUserIsAdminOf} from '../../../../../../store/data/teams/teamsSelectors';
import {getHasNewTeammateInvite} from '../../../../../../store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {navigateToAddTeammateAction} from '../../../../../../store/stateless/navigationActions';
import {AssignOptions, LazyAssignDropdown} from './lazyAssignDropdown';

/*
 * Props.
 */

interface AssignDropdownContainerProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  inboxesParameters: InboxesParameters;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;
  interfaceEventSource: ViewerDropdownInterfaceEventSourcesEnum;
}

/*
 * Selectors.
 */

function getConversations(_state: AppState, props: AssignDropdownContainerProps) {
  return props.conversations;
}

function makeGetConversationInboxIds() {
  return createSelector([getUserOwnedInboxes, getConversations], (userOwnedInboxes, conversations) => {
    const [teammateConversations, teamConversations] = partition(conversations, isTeammateConversation);
    const firstTeamNamespace = teamConversations[0] && teamConversations[0].namespace;

    const isAllPrivate = teammateConversations.length > 0 && teamConversations.length === 0;
    const isAllSameTeam = teamConversations.every((tc) => tc.namespace === firstTeamNamespace);

    // If every conversation is a private inbox, return all the user's participating team inboxIds.
    if (isAllPrivate) {
      return userOwnedInboxes.map((i) => i.id);
    }

    // If every conversation is from the same team, return all the conversations' inboxIds.
    if (isAllSameTeam) {
      const conversationInboxIds = teamConversations.map((c) => c.inboxIds);
      return intersection(...conversationInboxIds);
    }

    // If the conversations span multiple conversations in mixed teams, return null.
    return null;
  });
}

const getBuildAddTeammateSettingsPath = createSelector(
  [getIsInTrial, getFirstTeamUserIsAdminOf, getIsUserGlobalAdmin, getHasNewTeammateInvite],
  (isInTrial, firstTeamUserIsAdminOf, isUserGlobalAdmin, hasNewTeammateInvite) =>
    (hasOtherAssignableTeammates: boolean) => {
      // If the user is not in trial and has teammates shown in the dropdown,
      // hide.
      if (!isInTrial && hasOtherAssignableTeammates) {
        return undefined;
      }

      // Otherwise, they are in trial, so we should only show if the user can
      // invite teammates.

      // If the user is a global admin, they can always add teammates to the
      // conversations.
      if (isUserGlobalAdmin) {
        return buildAddTeammateSettingsPath(hasNewTeammateInvite);
      }

      // Otherwise, they're not a global admin.
      // If they're not even a team admin, then we should hide the card because
      // they can't invite anyone.
      if (!firstTeamUserIsAdminOf) {
        return undefined;
      }

      return buildAddTeammateSettingsPath(hasNewTeammateInvite, firstTeamUserIsAdminOf.namespace);
    },
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AssignDropdownContainerProps>()(() => {
  const getAssigneeAll = makeGetAssigneeForAll(getConversations);

  return {
    user: getUser,
    isTeamMember: makeGetIsTeamMemberForConversations(getConversations),
    findOnlineStatus: getFindOnlineStatus,
    buildAddTeammateSettingsPath: getBuildAddTeammateSettingsPath,
    assigneeAll: getAssigneeAll,
    conversationInboxIds: makeGetConversationInboxIds(),
    companyUrl: getCompanyUrl,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<AssignDropdownContainerProps>()(() => ({
  assignToTeammate: assignToTeammateAction,
  assignToTeammateBulk: assignToTeammateBulkAction,
  navigateToAddTeammateAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: AssignDropdownContainerProps,
) {
  const {
    panelId,
    cellsContext,
    conversations,
    isBulkActionRequired,
    inboxesParameters,
    onInterfaceEvent,
    interfaceEventSource,
  } = ownProps;
  const {buildAddTeammateSettingsPath: buildAddTeammateSettingsPathProp, assigneeAll} = stateProps;

  const onAssign: HandlerOf<AssignOptions> = (params: AssignOptions) => {
    const {teammate, openedAt, interfaceEventSection} = params;
    if (onInterfaceEvent) {
      onInterfaceEvent(
        isUndefined(teammate) ? BulkActionsTrackingTypesEnum.UNASSIGN : BulkActionsTrackingTypesEnum.ASSIGN,
        false,
      );
    }

    // If the new assignee is the same, nothing to do.
    if (teammate === assigneeAll) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.P3_ASSIGN_USING_DROPDOWN, {
      num_conversations: conversations.length,
      teammate_id: teammate ? teammate.id : null,
      time_til_assign: performance.now() - (openedAt ?? 0),
      section: interfaceEventSection,
      is_bulk: isBulkActionRequired,
      location: interfaceEventSource,
    });

    // If the specified teammate is undefined, unassign.
    const conversationIds = conversations.map((c) => c.id);
    if (isUndefined(teammate)) {
      dispatchProps.assignToTeammate(conversationIds, undefined, {cellsContext});
      return;
    }

    // Otherwise, assign.
    dispatchProps.assignToTeammate(conversationIds, teammate.id, {cellsContext});
  };

  const onBulkAssign: HandlerOf<AssignOptions> = (params: AssignOptions) => {
    const {teammate, openedAt, interfaceEventSection} = params;
    if (onInterfaceEvent) {
      onInterfaceEvent(
        isUndefined(teammate) ? BulkActionsTrackingTypesEnum.UNASSIGN : BulkActionsTrackingTypesEnum.ASSIGN,
        true,
      );
    }

    // If the new assignee is the same, nothing to do.
    if (teammate === assigneeAll) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.P3_ASSIGN_USING_DROPDOWN, {
      num_conversations: conversations.length,
      teammate_id: teammate ? teammate.id : null,
      time_til_assign: performance.now() - (openedAt ?? 0),
      section: interfaceEventSection,
      is_bulk: isBulkActionRequired,
      location: interfaceEventSource,
    });

    // If the specified teammate is undefined, unassign.
    if (isUndefined(teammate)) {
      dispatchProps.assignToTeammateBulk(inboxesParameters, cellsContext, undefined);
      return;
    }

    // Otherwise, assign.
    dispatchProps.assignToTeammateBulk(inboxesParameters, cellsContext, teammate.id);
  };

  // Should only be able to add a teammate if there's an add teammate settings
  // path.
  const buildNavigateToAddTeammate = (hasOtherAssignableTeammates: boolean) => {
    const addTeammateSettingsPath = buildAddTeammateSettingsPathProp(hasOtherAssignableTeammates);
    if (!addTeammateSettingsPath) {
      return undefined;
    }

    return () =>
      dispatchProps.navigateToAddTeammateAction(
        panelId,
        addTeammateSettingsPath,
        InviteTeammatePopoverAccessPointsEnum.ASSIGN_DROPDOWN,
      );
  };

  return {
    ...stateProps,
    ...ownProps,
    onAssign,
    onBulkAssign,
    assigneeAll,
    buildNavigateToAddTeammate,
  };
}

export const LazyAssignDropdownContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(LazyAssignDropdown);
